import { LoadingButton } from "@mui/lab";
import {
    Button,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import {
    PageHeader,
} from "../../../components";
import { renderControls, RequestStateChecker } from "../../../functions";

import DashboardLayout from "../../../layouts/Dashboard";
import {
    workflowServiceWithoutActRequest, workflowWithoutDepartureRequest,
} from "../../../redux/actions";
import {
    getServiceWithoutActRequestByKey,
} from "../../../redux/actions";
import {
    ServiceWithoutActRequestInfoSection,
} from "./Sections";
import { CONTRACT_REQUEST, SERVICE_WITHOUT_ACT_REQUEST } from "../../../routes";

import {protocolCRUD, resultCRUD, serviceWithoutActRequestCRUD} from "../../../http";
import {REQUEST_STATE_ID_CREATED} from "../../../redux/constants/requestStateConstants";
import {confirmDialog} from "../../../components/Dialog/DialogDelete";
import { commentDialog } from "../../../components/Dialog/CommentDialog";
import { mapReferralSpecificationsForSubmit } from "../../../functions/viewDataMap";
import useDialogs from "../../../components/Dialog/useDialogs";
import { validateReferralSpecificationsForSubmit } from "../../../functions/validations";
import { REQUEST_TYPE_ID_ServiceWithoutActRequest } from "../../../redux/constants/requestTypeConstants";

const uniqueBy = (items, getKey) => {
    const seen = new Set();
    return (items || []).filter((item) => {
        const key = getKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const specificationUniqueKey = (specification) => {
    const serviceId = specification?.serviceId ?? specification?.service?.id ?? "";
    const objectId = specification?.objectId ?? specification?.object?.id ?? "";
    const serviceKindId = specification?.serviceKindId ?? specification?.serviceKind?.id ?? "";
    const unitId = specification?.unitId ?? specification?.unit?.id ?? "";
    const scheduleId = specification?.scheduleId ?? specification?.schedule?.id ?? "";
    const price = specification?.price ?? "";
    const count = specification?.count ?? "";

    return [serviceId, objectId, serviceKindId, unitId, scheduleId, price, count].join("|");
};

export const ServiceWithoutActRequestInfo = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { alert, DIALOG } = useDialogs();

    const [loading, setLoading] = useState(false);
    const [validationError, setValidationError] = useState(false);

    const { serviceWithoutActRequest } = useSelector(
        (s) => s.serviceWithoutActRequest
    );

    const state = useMemo(
        () => new RequestStateChecker(serviceWithoutActRequest?.state?.code),
        [serviceWithoutActRequest?.state?.code]
    );

    const [request, setRequest] = useState(serviceWithoutActRequest);
    const [requestId] = useState(id);
    const [stateId, setStateId] = useState(0);
    const [results, setResults] = useState([]);
    const [protocols, setProtocols] = useState([]);
    const [expertResults, setExpertResults] = useState([]);

    const [createResultOpen, setCreateResultOpen] = useState(false);
    const [protocolAddOpen, setProtocolAddOpen] = useState(false);
    const [expertAddOpen, setExpertAddOpen] = useState(false);
    

    const stateCode = serviceWithoutActRequest?.state?.code?.toLowerCase();

    const validateResearchList = () => {
        const valid = validateReferralSpecificationsForSubmit(
            request?.referralSpecifications,
            null,
            null,
            alert,
            REQUEST_TYPE_ID_ServiceWithoutActRequest,
            null,
            request?.researchEndDateTime,
            null
        );
        setValidationError(!valid);
        return valid;
    };

    const handleEdit = async () => {
        const normalizedRequest = {
            ...request,
            objects: uniqueBy(request?.objects || [], (item) => item?.id),
            specifications: uniqueBy(request?.specifications || [], specificationUniqueKey),
            referralSpecifications: mapReferralSpecificationsForSubmit(request?.referralSpecifications || []),
        };

        delete normalizedRequest.state;
        delete normalizedRequest.department;
        delete normalizedRequest.responsible;
        delete normalizedRequest.reponsiblePerformer;
        delete normalizedRequest.createdBy;
        delete normalizedRequest.results;
        delete normalizedRequest.protocols;

        await serviceWithoutActRequestCRUD.edit(normalizedRequest)
    }

    const handleWorkflow = async (e, nextStateId, comment) => {
        e.preventDefault();

        // Завершение (research -> completed): список исследований обязателен
        if (state.isResearch() && nextStateId === 4) {
            if (!validateResearchList()) {
                return;
            }
        }

        setLoading(true);

        try {
            await handleEdit();

            if (!serviceWithoutActRequest?.state?.id) {
                return;
            }

            const prevStateId = serviceWithoutActRequest.state.id;

            // Updating Workflow
            const updated = await dispatch(
                workflowServiceWithoutActRequest(id, prevStateId, nextStateId, comment)
            );

            if (updated) {
                setStateId(nextStateId);
            } else {
                // Если workflow не применился, всё равно перезагружаем данные
                await fetch();
            }
        } catch (e) {
            console.error('Ошибка при смене статуса:', e);
        } finally {
            setLoading(false);
        }
    };


    const handleWorkflowDeclinedToCreate = async () => {
        setLoading(true);

        if (!serviceWithoutActRequest?.state?.id) return setLoading(false);

        const prevStateId = serviceWithoutActRequest.state.id;
        const nextStateId = state.getId(REQUEST_STATE_ID_CREATED);

        // Updating Workflow
        const updated = await dispatch(
            workflowServiceWithoutActRequest(requestId, prevStateId, nextStateId)
        );
        if (!updated) return setLoading(false);

        setStateId(nextStateId);
        setLoading(false);
    };

    const controls = [
        {
            state: "created",
            roles: [],
            buttons: [
                <LoadingButton
                    loading={loading}
                    color="primary"
                    onClick={(e) => handleWorkflow(e, 2)}
                >
                    Начать обработку
                </LoadingButton>,
                <Button
                    color="secondary"
                    onClick={handleEdit}
                >
                    Сохранить
                </Button>,
                <Button
                    variant={"text"}
                    onClick={() => history.push(SERVICE_WITHOUT_ACT_REQUEST)}
                >
                    Вернуться
                </Button>
            ],
        },
        {
            state: "processing",
            roles: [],
            buttons: [
                <LoadingButton
                    loading={loading}
                    color="primary"
                    onClick={(e) => handleWorkflow(e, 3)}
                >
                    Начать исследование
                </LoadingButton>,
                <Button
                    color="secondary"
                    onClick={handleEdit}
                >
                    Сохранить
                </Button>,
                <Button
                    variant={"text"}
                    onClick={() => history.push(SERVICE_WITHOUT_ACT_REQUEST)}
                >
                    Вернуться
                </Button>
            ],
        },
        {
            state: "research",
            roles: [],
            buttons: [
                <LoadingButton
                    variant="outlined"
                    onClick={() => setCreateResultOpen(true)}
                    loading={loading}
                >
                    Добавить в журнал
                </LoadingButton>,
                <LoadingButton
                    variant="outlined"
                    onClick={() => setProtocolAddOpen(true)}
                    loading={loading}
                >
                    Создать протокол
                </LoadingButton>,
                <LoadingButton
                    style={{ marginLeft: '5px' }}
                    variant={"outlined"}
                    loading={loading}
                    onClick={() => setExpertAddOpen(true)}
                // disabled={!hasResults || !hasProtocols}
                >
                    Создать экспертное заключение
                </LoadingButton>,
                <LoadingButton
                    loading={loading}
                    color="primary"
                    onClick={(e) => {
                        if (!validateResearchList()) return;
                        confirmDialog('Завершение', 'Вы действительно хотите завершить эту заявку?', async () => {
                            await handleWorkflow(e, 4);
                        });
                    }}
                >
                    Завершить
                </LoadingButton>,
                <LoadingButton color="error" onClick={async (e) => {
                    commentDialog('Изменение статуса', 'Укажите комментарий для отклонения.', async (_) => {
                        await handleWorkflow(e, 5, _?.comment);
                    });                    
                }}>
                    Отклонить с комментариями
                </LoadingButton>,
                <Button
                    color="secondary"
                    onClick={async () => {
                        if (!validateResearchList()) return;
                        await handleEdit();
                    }}
                >
                    Сохранить
                </Button>,
                <Button
                    variant={"text"}
                    onClick={() => history.push(SERVICE_WITHOUT_ACT_REQUEST)}
                >
                    Вернуться
                </Button>
            ],
        },
        {
            state: "completed",
            roles: [],
            buttons: [
                <Button
                    variant={"text"}
                    onClick={() => history.push(SERVICE_WITHOUT_ACT_REQUEST)}
                >
                    Вернуться
                </Button>
            ]
        },
        {
            state: "declined",
            roles: [],
            buttons: [
                <LoadingButton
                    loading={loading}
                    onClick={() => {
                        confirmDialog('Пересоздание', 'Вы действительно хотите пересоздать заявку?', async () => {
                            await handleWorkflowDeclinedToCreate()
                        })
                    }
                    }
                >
                    Пересоздать
                </LoadingButton>,
                <Button
                    variant={"text"}
                    onClick={() => history.push(CONTRACT_REQUEST)}
                >
                    Вернуться
                </Button>
            ],
        }
    ];

    const fetch = async () => {
        setLoading(true);

        const response = await dispatch(getServiceWithoutActRequestByKey(requestId));
        
        // Удаляем дубликаты спецификаций после загрузки данных
        const normalizedResponse = {
            ...response,
            objects: uniqueBy(response?.objects || [], (item) => item?.id),
            specifications: uniqueBy(response?.specifications || [], specificationUniqueKey),
        };
        
        setRequest(normalizedResponse);

        const resultsResponse = await resultCRUD.search({
            paging: { skip: 0 },
            filter: {
                serviceWithoutActRequestId: {
                    operand1: response.id,
                    operator: 'equals'
                }
            }
        });
        const protocolResponse = await protocolCRUD.search({
            paging: { skip: 0 },
            filter: {
                serviceWithoutActRequestId: {
                    operand1: response.id,
                    operator: 'equals'
                }
            }
        });
        setProtocols(protocolResponse.result);
        setResults(resultsResponse.result);

        setLoading(false);
    }

    useEffect(() => {
        async function fetchData() {
            await fetch();
        }

        fetchData();
    }, [dispatch, requestId, stateId]);

    return (
        <DashboardLayout>
            <DIALOG />
            <PageHeader
                heading={`Заявка без акта №${id} ${stateCode ? `(${t(`dashboard.state.${stateCode}`)})` : ""
                    }`}
                status={serviceWithoutActRequest.state?.name}
                controls={renderControls(controls, {
                    state: stateCode,
                    roles: [],
                })}
            />
            <ServiceWithoutActRequestInfoSection data={request} fetch={fetch}
                results={results}
                protocols={protocols}
                expertResults={expertResults}
                setData={(next) => {
                    if (validationError) setValidationError(false);
                    setRequest(next);
                }}
                validationError={validationError}
                createResultOpen={createResultOpen}
                setCreateResultOpen={setCreateResultOpen}
                protocolAddOpen={protocolAddOpen}
                setProtocolAddOpen={setProtocolAddOpen}
                expertAddOpen={expertAddOpen}
                setExpertAddOpen={setExpertAddOpen}
            />
        </DashboardLayout>
    );
};
